/**
 * normalizePolicies - Normalizes policy data for display in Policy Comparison UI
 *
 * Rules:
 * - Split inflation: COB rate grows monthly benefits; Face rate grows pool/totals
 * - If face rate is 0/undefined, pools/totals fall back to COB rate (with COB cap years)
 * - Single inflation: same rate for both monthly and pool
 * - Years used for CALC = min(yearsUntilClaim, capYears)
 * - Years shown in LABEL = capYears (when known)
 * - Step plans: apply full step increments only
 */

import { fundingPolicyTypeDefs, CarePhase, FundingPolicyType, formatCurrency, CARRIER_NAMES } from '@shared';
import { parseBioPercentToInflationRate } from '../util/policyQuoteCalculations';

interface NormalizedPolicy {
  policyId: string;
  policyType?: number;
  carrierName?: string;
  productName?: string;
  monthlyBenefitToday?: string;
  monthlyBenefitAtStart?: string;
  totalBenefitTodayByDuration?: string;
  totalBenefitAtStartByDuration?: string;
  growthLabel?: string;
  benefitPeriodMonths?: number;
  eliminationPeriodLabel?: string;
  planDesign?: string;
  methodBenefitsPaid: 'Indemnity' | 'Reimbursement';
  premiumMonthlyCost?: number;
  lumpSumPayment?: number;
}

type PolicyMap = Record<string, any>;

const POLICY_TYPES = [
  {
    types: [2904],
    label: 'Short‑Term Care',
  },
  {
    types: [2912],
    label: 'Type A CCRC Contract',
  },
  {
    types: [2925, 2926],
    label: 'Deferred Annuity with Income Multiplier',
  },
  {
    types: [2902, 2908, 2927],
    label: 'Life with Benefits Rider',
  },
  {
    types: [2905, 2907, 2922, 2923, 2924],
    label: 'Annuity-Based Hybrid LTC',
  },
  {
    types: [2903, 2906, 2910, 2911, 2914, 2916, 2917, 2918, 2919, 2920, 2921],
    label: 'Asset‑Based Hybrid LTC',
  },
  {
    types: [2901, 2913, 2915, 2928, 2929, 2930, 2931, 2932, 2933, 2934, 2937],
    label: 'Traditional Stand‑Alone LTC',
  },
];

const UNLIMITED_NUMBER = 999999999;

function getPolicyType(type: number) {
  const policy = POLICY_TYPES.find(({ types }) => types.includes(type));

  return policy?.label;
}

function formatBenefit(value?: number) {
  if (value === undefined || value === null) return '-';
  return value >= UNLIMITED_NUMBER ? 'Unlimited' : formatCurrency(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function compoundFutureValue(
  presentValue?: number,
  annualRate?: number,
  years?: number,
): number | undefined {
  if (
    !isFiniteNumber(presentValue) ||
    !isFiniteNumber(annualRate) ||
    !isFiniteNumber(years) ||
    years <= 0
  ) {
    return presentValue;
  }

  return presentValue * Math.pow(1 + annualRate, years);
}

function simpleFutureValue(
  presentValue?: number,
  annualRate?: number,
  years?: number,
): number | undefined {
  if (
    !isFiniteNumber(presentValue) ||
    !isFiniteNumber(annualRate) ||
    !isFiniteNumber(years) ||
    years <= 0
  ) {
    return presentValue;
  }

  return presentValue * (1 + annualRate * years);
}

/** Reads % from quote; returns number like 2 for "2%" */
function extractInflationRateFromQuote(sourceQuote: any): number {
  const explicitPercentString = sourceQuote?.aobInflation;

  if (explicitPercentString != null && !isNaN(parseFloat(explicitPercentString))) {
    return parseFloat(explicitPercentString);
  }

  const optionText: string | undefined = sourceQuote?.inflationBenefitOption;

  if (!optionText || optionText === 'None') return 0;

  const numericMatch = optionText.match(/(\d+(\.\d+)?)/);

  return numericMatch?.[0] ? parseFloat(numericMatch[0]) : 0;
}

/** Tries to read cap years from quote text ("20 Years", "…for 20 years") */
function extractInflationCapYearsFromQuote(sourceQuote: any): number | null {
  const optionText: string | undefined = sourceQuote?.inflationBenefitOption;

  if (optionText) {
    const capMatch = optionText.match(/for\s+(\d+)\s+years?/i);

    if (capMatch?.[1]) return parseInt(capMatch[1], 10);
  }

  const durationText = sourceQuote?.inflationDuration;

  if (durationText != null) {
    const durationMatch = String(durationText).match(/(\d+(\.\d+)?)\s*years?/i);

    if (durationMatch?.[1]) return parseFloat(durationMatch[1]);
  }

  if (sourceQuote?.aobInflation && sourceQuote?.cobInflation) return 20;

  return null;
}

const defaultsToIndemnityPayment = (carrierName?: string) => carrierName !== CARRIER_NAMES.ONE_AMERICA;

const getHomeCareWaitingPeriodDays = (carrierName?: string, fallbackDays?: number) =>
  carrierName === CARRIER_NAMES.ONE_AMERICA ? 0 : (fallbackDays ?? 90);

/** 0.02, 2, "2", "2%" → 0.02 */
function toNormalizedRate(inputRate: unknown): number | undefined {
  if (inputRate == null) return undefined;

  if (typeof inputRate === 'string') {
    const trimmed = inputRate.trim();

    if (!trimmed || trimmed.toLowerCase() === 'none' || trimmed === 'No BIO') return undefined;

    if (trimmed.includes('%')) {
      const viaBioRate = parseBioPercentToInflationRate(trimmed);

      if (Number.isFinite(viaBioRate)) return viaBioRate as number;
    }

    const numericValue = parseFloat(trimmed.replace('%', ''));

    if (!Number.isFinite(numericValue)) return undefined;

    return numericValue >= 1 ? numericValue / 100 : numericValue;
  }

  if (typeof inputRate === 'number' && Number.isFinite(inputRate)) {
    return inputRate >= 1 ? inputRate / 100 : inputRate;
  }

  return undefined;
}

function parseDurationToMonths(durationText?: string | null): number {
  if (!durationText || typeof durationText !== 'string') return 0;

  const durationMatch = durationText
    .trim()
    .toLowerCase()
    .match(/([\d.]+)\s*(year|years|yr|yrs|month|months)/i);

  if (!durationMatch) return 0;

  const quantity = parseFloat(durationMatch[1]);

  if (!Number.isFinite(quantity)) return 0;

  const unit = durationMatch[2];

  return /month/.test(unit) ? Math.round(quantity) : Math.round(quantity * 12);
}

function formatPercentLabel(rateOrRaw?: number | unknown): string {
  const normalizedRate = toNormalizedRate(rateOrRaw);

  if (!isFiniteNumber(normalizedRate)) return 'N/A';

  const percent = normalizedRate * 100;

  return percent % 1 === 0 ? `${percent.toFixed(0)}%` : `${percent.toFixed(1)}%`;
}

const formatYearsLabel = (inputYears: number) => {
  const roundedToTenth = Math.floor(inputYears * 10) / 10;

  return roundedToTenth % 1 === 0 ? String(Math.floor(roundedToTenth)) : String(roundedToTenth);
};

function parseSourceQuote(sourceQuoteData: any) {
  if (!sourceQuoteData) return {};

  try {
    return typeof sourceQuoteData === 'string' ? JSON.parse(sourceQuoteData) : sourceQuoteData;
  } catch {
    return {};
  }
}

function resolveProductName(sourceProductName?: string, policyTypeKey?: string) {
  if (sourceProductName) return sourceProductName;

  const typeDef = policyTypeKey ? fundingPolicyTypeDefs[policyTypeKey] : undefined;

  return typeDef ? typeDef.label : 'Insurance';
}

export function normalizePolicies(
  policyMap: PolicyMap,
  options: {
    client?: Client;
    returnOnInvestmentCalculationType?: string;
    yearsToStart?: number;
    daysPerMonthForDailyCalc?: number;
  } = {},
): NormalizedPolicy[] {
  const daysPerMonthForCalc = options.daysPerMonthForDailyCalc ?? 30;

  const yearsUntilClaim = (() => {
    const currentYear = new Date().getFullYear();
    const inferredStartYear = options.client?.appliedInferenceSet?.ltcAtYear;

    return inferredStartYear
      ? Math.max(0, inferredStartYear - currentYear)
      : (options.yearsToStart ?? 20);
  })();

  return Object.entries(policyMap).map(([policyId, policyData]) => {
    const sourceQuote = parseSourceQuote(policyData?.sourceQuote);
    const carrierName: string | undefined = sourceQuote?.carrierName;
    const planDesignFromQuote: string | undefined = sourceQuote?.policyType;
    const productName = resolveProductName(sourceQuote?.productName, policyData?.policyType);
    const policyType = policyData?.policyType as FundingPolicyType | undefined;

    const benefitsPaymentMethod: 'Indemnity' | 'Reimbursement' =
      policyData?.isIndemnityPolicyPayment != null
        ? policyData.isIndemnityPolicyPayment
          ? 'Indemnity'
          : 'Reimbursement'
        : defaultsToIndemnityPayment(carrierName)
          ? 'Indemnity'
          : 'Reimbursement';

    const homeCareWaitingDays = getHomeCareWaitingPeriodDays(
      carrierName,
      policyData?.homeCareWaitingPeriodDays,
    );
    const facilityCareWaitingDays = policyData?.facilityCareWaitingPeriodDays as number | undefined;

    let eliminationPeriodLabel: string | undefined;

    if (isFiniteNumber(homeCareWaitingDays) || isFiniteNumber(facilityCareWaitingDays)) {
      const homeDays = isFiniteNumber(homeCareWaitingDays) ? homeCareWaitingDays : 0;
      const facilityDays = isFiniteNumber(facilityCareWaitingDays) ? facilityCareWaitingDays : 0;

      eliminationPeriodLabel =
        homeDays === facilityDays
          ? `${homeDays} days`
          : `${homeDays} days home care / ${facilityDays} days facility care`;

      const isCareMattersPolicy =
        policyType === FundingPolicyType.careMattersTwo ||
        policyType === FundingPolicyType.jointCareMatters;

      if (isCareMattersPolicy) eliminationPeriodLabel += ' (paid retroactively in month 4)';
    }

    let benefitPeriodMonths: number | undefined = isFiniteNumber(
      sourceQuote?.ltcBenefitDurationMonths,
    )
      ? sourceQuote.ltcBenefitDurationMonths
      : undefined;

    if (!isFiniteNumber(benefitPeriodMonths)) {
      const totalMonthsFromDurations =
        parseDurationToMonths(sourceQuote?.aobDuration) +
        parseDurationToMonths(sourceQuote?.cobDuration);

      if (totalMonthsFromDurations > 0) benefitPeriodMonths = totalMonthsFromDurations;
    }

    let monthlyBenefitToday: number | undefined;
    let dailyBenefitToday: number | undefined;

    const phaseCalcsForPolicy = options.client?.policyPhaseCalculations?.[policyId];

    const fullCarePhaseCalc = Array.isArray(phaseCalcsForPolicy)
      ? phaseCalcsForPolicy
          .filter(Boolean)
          .find(
            (calc: any) => calc && typeof calc === 'object' && calc?.phase === CarePhase.fullCare,
          )
      : undefined;

    if (isFiniteNumber(fullCarePhaseCalc?.dailyBenefitAmount)) {
      dailyBenefitToday = fullCarePhaseCalc.dailyBenefitAmount;
      monthlyBenefitToday = dailyBenefitToday * daysPerMonthForCalc;
    }

    if (!isFiniteNumber(monthlyBenefitToday) && sourceQuote?.ltcBenefitMonthlyIndividual != null) {
      const monthlyFromQuote = Number(sourceQuote.ltcBenefitMonthlyIndividual);

      if (Number.isFinite(monthlyFromQuote)) {
        monthlyBenefitToday = monthlyFromQuote;
        dailyBenefitToday = monthlyFromQuote / daysPerMonthForCalc;
      }
    }

    if (!isFiniteNumber(monthlyBenefitToday)) {
      const dailyBenefitCandidates: number[] = [
        policyData?.dailyNursingHomeBenefitAmount,
        policyData?.dailyAssistedLivingBenefitAmount,
        policyData?.dailyHomeBenefitAmount,
        policyData?.dailyIndependentLivingBenefitAmount,
      ].filter(isFiniteNumber);

      if (dailyBenefitCandidates.length) {
        dailyBenefitToday = Math.max(...dailyBenefitCandidates);
        monthlyBenefitToday = dailyBenefitToday * daysPerMonthForCalc;
      }
    }

    const inflationRateFromQuotePercent = extractInflationRateFromQuote(sourceQuote);
    const hasFaceAmountInflation = policyData?.policyInflationProtectionOnFaceAmount != null;
    const hasCobAmountInflation = policyData?.policyInflationProtectionOnCOBAmount != null;
    const hasSplitInflation = hasFaceAmountInflation || hasCobAmountInflation;

    const isSimpleInflation =
      !!policyData?.isSimpleInflationProtection ||
      (typeof sourceQuote?.inflationBenefitOption === 'string' &&
        sourceQuote.inflationBenefitOption.toLowerCase().includes('simple'));

    const unifiedInflationRate =
      inflationRateFromQuotePercent > 0
        ? inflationRateFromQuotePercent / 100
        : toNormalizedRate(policyData?.policyInflationProtection);

    let faceInflationRate: number | undefined = hasSplitInflation
      ? hasFaceAmountInflation
        ? toNormalizedRate(policyData.policyInflationProtectionOnFaceAmount)
        : (unifiedInflationRate ?? 0)
      : unifiedInflationRate;

    let cobInflationRate: number | undefined = hasSplitInflation
      ? hasCobAmountInflation
        ? toNormalizedRate(policyData.policyInflationProtectionOnCOBAmount)
        : (unifiedInflationRate ?? 0)
      : unifiedInflationRate;

    if (!isFiniteNumber(cobInflationRate)) {
      cobInflationRate =
        inflationRateFromQuotePercent > 0 ? inflationRateFromQuotePercent / 100 : cobInflationRate;
    }

    const capYearsFromQuote = extractInflationCapYearsFromQuote(sourceQuote);
    const genericCapYears =
      (Number.isFinite(capYearsFromQuote as number) ? (capYearsFromQuote as number) : undefined) ??
      policyData?.policyInflationProtectionYears ??
      policyData?.policyInflationPeriodYears;

    const cobCapYearsExplicit =
      policyData?.policyInflationProtectionOnCOBYears ??
      policyData?.policyInflationProtectionOnCOBPeriodYears;

    const faceCapYearsExplicit =
      policyData?.policyInflationProtectionOnFaceYears ??
      policyData?.policyInflationProtectionOnFacePeriodYears;

    const isOneAmericaPolicy = [
      FundingPolicyType.assetCare,
      FundingPolicyType.jointAssetCare,
      FundingPolicyType.annuityCareTwo,
      FundingPolicyType.assetcareThree,
      FundingPolicyType.jointAssetcareThree,
    ].includes(policyType as FundingPolicyType);

    const cobCapYears: number | undefined = isFiniteNumber(cobCapYearsExplicit)
      ? cobCapYearsExplicit
      : isOneAmericaPolicy && hasSplitInflation
        ? 20
        : isFiniteNumber(genericCapYears)
          ? (genericCapYears as number)
          : undefined;

    const faceCapYears: number | undefined = isFiniteNumber(faceCapYearsExplicit)
      ? faceCapYearsExplicit
      : isFiniteNumber(genericCapYears)
        ? (genericCapYears as number)
        : undefined;

    const cobYearsApplied = Math.min(
      yearsUntilClaim,
      isFiniteNumber(cobCapYears) ? cobCapYears! : yearsUntilClaim,
    );
    const faceYearsApplied = Math.min(
      yearsUntilClaim,
      isFiniteNumber(faceCapYears) ? faceCapYears! : yearsUntilClaim,
    );

    // Decide which rate/years to use for pools & totals:
    // Prefer face rate if > 0; else fall back to COB (with COB years cap). If both invalid, keep face (could be 0).
    const poolInflationRate: number | undefined = ((): number | undefined => {
      if (isFiniteNumber(faceInflationRate) && faceInflationRate! > 0) return faceInflationRate!;
      if (isFiniteNumber(cobInflationRate) && cobInflationRate! > 0) return cobInflationRate!;
      return faceInflationRate;
    })();

    const poolYearsApplied: number = ((): number => {
      if (isFiniteNumber(faceInflationRate) && faceInflationRate! > 0) return faceYearsApplied;
      if (isFiniteNumber(cobInflationRate) && cobInflationRate! > 0) return cobYearsApplied;
      return faceYearsApplied;
    })();

    const growthFunction = isSimpleInflation ? simpleFutureValue : compoundFutureValue;
    const monthlyBenefitAtStart =
      growthFunction(monthlyBenefitToday, cobInflationRate, cobYearsApplied) ?? monthlyBenefitToday;

    const longTermCareBenefitPoolToday = isFiniteNumber(policyData?.policyTotalLTCBenefit)
      ? policyData.policyTotalLTCBenefit
      : undefined;

    let longTermCareBenefitPoolAtStart: number | undefined;
    const isStepPlan = !!policyData?.isStepInflationProtection;

    if (isStepPlan && isFiniteNumber(longTermCareBenefitPoolToday)) {
      const stepIncreaseRate = toNormalizedRate(policyData?.policyInflationProtection);
      const stepIntervalYears =
        policyData?.policyInflationProtectionYears ?? policyData?.policyInflationPeriodYears;

      if (
        isFiniteNumber(stepIncreaseRate) &&
        isFiniteNumber(stepIntervalYears) &&
        stepIntervalYears > 0
      ) {
        const wholeStepsApplied = Math.floor(yearsUntilClaim / stepIntervalYears);
        longTermCareBenefitPoolAtStart =
          longTermCareBenefitPoolToday * Math.pow(1 + stepIncreaseRate, wholeStepsApplied);
      } else {
        longTermCareBenefitPoolAtStart = longTermCareBenefitPoolToday;
      }
    } else if (isFiniteNumber(longTermCareBenefitPoolToday) && isFiniteNumber(poolInflationRate)) {
      longTermCareBenefitPoolAtStart = growthFunction(
        longTermCareBenefitPoolToday,
        poolInflationRate,
        poolYearsApplied,
      );
    } else {
      longTermCareBenefitPoolAtStart = longTermCareBenefitPoolToday;
    }

    if (
      sourceQuote?.fundingPolicyType === FundingPolicyType.careScoutCareAssurance &&
      isFiniteNumber(sourceQuote?.benefitPool) &&
      isFiniteNumber(sourceQuote?.dailyBenefitAmount)
    ) {
      const poolFromQuote = Number(sourceQuote.benefitPool);
      const monthlyFromDaily = Number(sourceQuote.dailyBenefitAmount) * daysPerMonthForCalc;
      benefitPeriodMonths = Math.round(poolFromQuote / monthlyFromDaily);
    } else if (
      !isFiniteNumber(benefitPeriodMonths) &&
      isFiniteNumber(longTermCareBenefitPoolToday) &&
      isFiniteNumber(monthlyBenefitToday) &&
      monthlyBenefitToday > 0
    ) {
      benefitPeriodMonths = Math.round(longTermCareBenefitPoolToday / monthlyBenefitToday);
    }

    let totalBenefitTodayByDuration: number | undefined;
    let totalBenefitAtStartByDuration: number | undefined;

    const inflateTotalByPoolRate = (value?: number) =>
      isFiniteNumber(value) && isFiniteNumber(poolInflationRate)
        ? (isSimpleInflation ? simpleFutureValue : compoundFutureValue)(
            value,
            poolInflationRate,
            poolYearsApplied,
          )
        : value;

    if (
      sourceQuote?.ltcBenefitTotal != null &&
      Number.isFinite(Number(sourceQuote.ltcBenefitTotal))
    ) {
      const totalFromQuote = Number(sourceQuote.ltcBenefitTotal);
      totalBenefitTodayByDuration = totalFromQuote;
      totalBenefitAtStartByDuration = inflateTotalByPoolRate(totalFromQuote);
    }

    if (sourceQuote?.benefitPool != null && Number.isFinite(Number(sourceQuote.benefitPool))) {
      const poolFromQuote = Number(sourceQuote.benefitPool);
      totalBenefitTodayByDuration = poolFromQuote;
      totalBenefitAtStartByDuration = inflateTotalByPoolRate(poolFromQuote);
    }

    if (
      !isFiniteNumber(totalBenefitTodayByDuration) &&
      isFiniteNumber(benefitPeriodMonths) &&
      isFiniteNumber(monthlyBenefitToday)
    ) {
      totalBenefitTodayByDuration = monthlyBenefitToday * benefitPeriodMonths;
    }
    if (
      !isFiniteNumber(totalBenefitAtStartByDuration) &&
      isFiniteNumber(benefitPeriodMonths) &&
      isFiniteNumber(monthlyBenefitAtStart)
    ) {
      totalBenefitAtStartByDuration = monthlyBenefitAtStart * benefitPeriodMonths;
    }
    if (
      !isFiniteNumber(totalBenefitTodayByDuration) &&
      isFiniteNumber(longTermCareBenefitPoolToday)
    ) {
      totalBenefitTodayByDuration = longTermCareBenefitPoolToday;
    }
    if (
      !isFiniteNumber(totalBenefitAtStartByDuration) &&
      isFiniteNumber(longTermCareBenefitPoolAtStart)
    ) {
      totalBenefitAtStartByDuration = longTermCareBenefitPoolAtStart;
    }

    let growthLabel: string | undefined = sourceQuote?.inflationBenefitOption;

    if (!growthLabel || growthLabel === 'None') {
      if (hasSplitInflation) {
        const methodLabel = isSimpleInflation ? 'Simple' : 'Compound';
        const capYearsLabel = isFiniteNumber(cobCapYears)
          ? ` (${formatYearsLabel(cobCapYears)} yrs)`
          : '';
        growthLabel = `Face ${formatPercentLabel(faceInflationRate)}, COB ${formatPercentLabel(
          cobInflationRate,
        )} ${methodLabel}${capYearsLabel}`;
      } else if (isFiniteNumber(unifiedInflationRate) && unifiedInflationRate > 0) {
        const methodLabel = isSimpleInflation ? 'Simple' : 'Compound';
        growthLabel =
          !isFiniteNumber(genericCapYears) || (genericCapYears as number) >= 100
            ? `${formatPercentLabel(unifiedInflationRate)} ${methodLabel} for life`
            : `${formatPercentLabel(unifiedInflationRate)} ${methodLabel} (${formatYearsLabel(
                genericCapYears as number,
              )} yrs)`;
      }
    }

    const planDesign = getPolicyType(policyData.policyType) || planDesignFromQuote || productName;

    return {
      policyId,
      planDesign,
      carrierName,
      productName,
      growthLabel,
      benefitPeriodMonths,
      eliminationPeriodLabel,
      policyType: policyData.policyType,
      methodBenefitsPaid: benefitsPaymentMethod,
      lumpSumPayment: policyData?.policyLumpSumPayment,
      premiumMonthlyCost: policyData?.policyPremiumMonthlyCost,
      monthlyBenefitToday: formatBenefit(monthlyBenefitToday),
      monthlyBenefitAtStart: formatBenefit(monthlyBenefitAtStart),
      totalBenefitTodayByDuration: formatBenefit(totalBenefitTodayByDuration),
      totalBenefitAtStartByDuration: formatBenefit(totalBenefitAtStartByDuration),
    };
  });
}
